/**
 * Annotative scale context — discriminated union (Phase PR-1)
 *
 * AutoCAD stores per-annotation-scale "context data" objects in the OBJECTS
 * section of a DXF (`AcDbAnnotScaleObjectContextData` and friends — one
 * concrete subclass per entity type that supports annotative behaviour:
 * TEXT, MTEXT, MTEXT-as-attribute, ATTRIB, INSERT, HATCH, DIMENSION,
 * MULTILEADER, LEADER, TOLERANCE).
 *
 * Each context carries the visual fields that change with scale (insertion,
 * rotation, height, scaleX/Y, attachment, definition points, ...) so the
 * renderer can pick the proper entry by `scaleName` without re-deriving
 * anything at draw time. The on-disk class hierarchy is closed; we mirror it
 * as a TypeScript discriminated union keyed on `kind`, which gives us
 * compile-time exhaustiveness checks and matches the existing pattern used
 * by `mleaderContextDataSchema` and `annotativeContextSchema` in
 * model.ts.
 *
 * ## Why this lives in its own module
 *
 * `model.ts` is already 1404 lines and approaching the 2000-line hard limit.
 * The annotative discriminated union is large (10 variants × ~5 fields each)
 * and conceptually self-contained. Hosting it here keeps `model.ts` from
 * crossing the limit during PR-1 and avoids an immediate split.
 *
 * ## Why we DO NOT import `drawingPointSchema` from ./model
 *
 * `model.ts` imports `annotScaleContextSchema` (this file) into its TEXT,
 * MTEXT, HATCH and ATTDEF entity schemas. If we then imported
 * `drawingPointSchema` back from `./model`, the cycle would be:
 *
 *     annotative.ts → model.ts → annotative.ts
 *
 * Even though TypeScript allows circular type-only imports, the runtime
 * Zod schema evaluation order is undefined under such cycles and can yield
 * `undefined` schema references inside `z.array(...)` calls (silent
 * `TypeError: Cannot read properties of undefined`). Per CLAUDE.md
 * priority #2 ("Граф зависимостей — красная зона") we cut this cycle by
 * inlining a minimal `annotScalePointSchema` here. The shape is identical
 * to `drawingPointSchema` — `{x, y}` — so consumers that already destructure
 * a 2D point work unchanged.
 *
 * ## Variant catalogue (per AutoCAD 2018+ ObjectARX reference)
 *
 *  1. text             — single-line TEXT (AcDbTextObjectContextData)
 *  2. mtext            — paragraph MTEXT (AcDbMTextObjectContextData)
 *  3. mtext_attribute  — MTEXT used as a multileader attribute
 *                        (AcDbMTextAttributeObjectContextData)
 *  4. attribute        — ATTRIB instance
 *                        (AcDbBlkRefObjectContextData)
 *  5. blockref         — INSERT instance
 *                        (AcDbBlkRefObjectContextData)
 *  6. hatch            — HATCH (AcDbHatchScaleContextData)
 *  7. dimension        — DIMENSION (AcDbDimensionObjectContextData and
 *                        subtype-specific subclasses)
 *  8. mleader          — MULTILEADER (AcDbMLeaderObjectContextData)
 *  9. leader           — LEADER (AcDbLeaderObjectContextData) — legacy
 * 10. tolerance        — TOLERANCE (AcDbFcfObjectContextData)
 *
 * The base fields (scaleName, scaleFactor, flags, ownerHandle) are common to
 * every variant per AutoCAD's `AcDbObjectContextData` superclass.
 */

import { z } from "zod";

/**
 * Minimal 2D point — kept local to break the import cycle with model.ts
 * (see file header). Identical shape to `drawingPointSchema` — interop
 * with consumer types is by structural equivalence.
 */
export const annotScalePointSchema = z.object({
  x: z.number(),
  y: z.number(),
});
export type AnnotScalePoint = z.infer<typeof annotScalePointSchema>;

/**
 * Common fields for every per-scale context. Mirrors the on-disk
 * `AcDbObjectContextData` superclass (group codes 100/70 + the parent
 * scale handle that resolves to an `AcDbScale` entry).
 *
 *  - `scaleName`   — Foreign key into `header.annotationScales[].name`.
 *                    Required and non-empty: an orphan reference is a
 *                    parser-level error (handled upstream — schema-level
 *                    we only insist it isn't blank).
 *  - `scaleFactor` — Numeric scale (e.g. 0.01 for 1:100, 50 for 1:50,
 *                    convention follows AutoCAD's CANNOSCALEVALUE).
 *                    Duplicated here for render-time perf so the resolver
 *                    can compute `effectiveHeight = base * scaleFactor`
 *                    without a catalogue lookup per draw call.
 *  - `flags`       — DXF group 70 on the context record. Round-trip only.
 *  - `defaultFlag` — DXF group 290 ("is-default-context-data" bool, Autodesk
 *                    R2018). Lives in the base `AcDbObjectContextData` subclass,
 *                    right after group 70. Round-trip only; optional so a source
 *                    without it stays byte-identical (no-emit). Mirrors the dxf-io
 *                    `AnnotScaleBase.defaultFlag` (structural-equivalence contract).
 *  - `ownerHandle` — DXF group 330 (handle of the parent extension dict).
 *                    Round-trip only — the importer fills it in to keep
 *                    handle linkage stable across save/reload.
 */
const annotScaleBaseSchema = z.object({
  scaleName: z.string().min(1),
  scaleFactor: z.number(),
  flags: z.number().default(0),
  defaultFlag: z.number().optional(),
  ownerHandle: z.string().optional(),
});

/**
 * 1. TEXT — `AcDbTextObjectContextData`.
 * Group codes: 10/20 insertion, 11/21 alignment, 50 rotation.
 */
export const annotScaleTextContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("text"),
  insertion: annotScalePointSchema,
  alignment: annotScalePointSchema.optional(),
  rotation: z.number(),
});

/**
 * 2. MTEXT — `AcDbMTextObjectContextData`.
 * Group codes: 10/20 insertion, 50 rotation, 41 reference width
 * (optional — 0 means "unbounded box"), 71 attachment (optional).
 */
export const annotScaleMtextContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("mtext"),
  insertion: annotScalePointSchema,
  rotation: z.number(),
  width: z.number().optional(),
  attachment: z.number().optional(),
});

/**
 * 3. MTEXT-as-attribute — `AcDbMTextAttributeObjectContextData`.
 * MTEXT used as the content of a multileader attribute. Carries an
 * `attribTag` matching the parent ATTDEF tag so the resolver can pair
 * the context to the right attribute when re-rendering.
 */
export const annotScaleMtextAttributeContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("mtext_attribute"),
  insertion: annotScalePointSchema,
  rotation: z.number(),
  attribTag: z.string().min(1),
});

/**
 * 4. ATTRIB — `AcDbBlkRefObjectContextData` (attribute-instance variant).
 * AutoCAD shares the BlkRef context-data class for both INSERT and ATTRIB;
 * we split them at the union level so `attribTag` stays type-safe.
 */
export const annotScaleAttributeContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("attribute"),
  insertion: annotScalePointSchema,
  rotation: z.number(),
  attribTag: z.string().min(1),
});

/**
 * 5. INSERT — `AcDbBlkRefObjectContextData` (block-reference variant).
 * Group codes: 10/20 insertion, 41/42 scale X/Y, 50 rotation.
 */
export const annotScaleBlockrefContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("blockref"),
  insertion: annotScalePointSchema,
  scaleX: z.number(),
  scaleY: z.number(),
  rotation: z.number(),
});

/**
 * 6. HATCH — `AcDbHatchScaleContextData`.
 * Hatch scales the pattern (not the boundary) per annotation scale —
 * the boundary loop stays in world coordinates regardless of CANNOSCALE.
 */
export const annotScaleHatchContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("hatch"),
  patternScale: z.number(),
  patternAngle: z.number(),
});

/**
 * 7. DIMENSION — `AcDbDimensionObjectContextData` (+ subtype-specific
 * subclasses such as `AcDbAlignedDimensionObjectContextData`).
 *
 * Definition points carry the AutoCAD-specific defPoint set per subtype:
 *  - aligned/linear : defPoint, extLine1, extLine2, textMidpoint
 *  - angular        : adds arcPoint
 *  - diametric/radial: chord midpoint + leader anchor
 *  - ordinate       : leader endpoints
 *
 * The shape stored here is intentionally subtype-aware so the writer can
 * round-trip without inferring from group codes. Subtype-specific extra
 * fields are deferred to PR-2/PR-7 alongside the importer.
 *
 * IMPORTANT: this is a **NEW** shape — it is not the same as the legacy
 * `annotativeContextSchema` in model.ts which DIMENSION currently uses.
 * The legacy field stays untouched in PR-1; PR-2/PR-7 will migrate
 * dimensions to the new union.
 */
export const annotScaleDimensionContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("dimension"),
  subtype: z.enum([
    "aligned",
    "linear",
    "angular",
    "diametric",
    "radial",
    "ordinate",
  ]),
  definitionPoints: z.array(annotScalePointSchema),
  textPosition: annotScalePointSchema.optional(),
});

/**
 * 8. MULTILEADER — `AcDbMLeaderObjectContextData`.
 *
 * Mirrors the existing `mleaderContextDataSchema` field set in model.ts
 * (textHeight, arrowheadSize, doglegLength, landingGap, textRotation,
 * textColor, textWidth) but reshaped under the union with the shared
 * base. The legacy schema in model.ts stays untouched in PR-1; PR-7
 * migrates MLEADER to the new union.
 */
export const annotScaleMleaderContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("mleader"),
  textHeight: z.number().optional(),
  arrowheadSize: z.number().optional(),
  doglegLength: z.number().optional(),
  landingGap: z.number().optional(),
  textRotation: z.number().optional(),
  textColor: z.string().optional(),
  textWidth: z.number().optional(),
});

/**
 * 9. LEADER — `AcDbLeaderObjectContextData` (legacy entity, pre-MLEADER).
 *
 * AutoCAD persists the polyline vertex list per scale — the leader can
 * physically reroute when CANNOSCALE changes. Vertex array stored
 * verbatim; resolver returns it as-is for the renderer.
 */
export const annotScaleLeaderContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("leader"),
  vertices: z.array(annotScalePointSchema),
});

/**
 * 10. TOLERANCE — `AcDbFcfObjectContextData` (Feature Control Frame).
 *
 * TOLERANCE entities are essentially a styled MTEXT block tied to a
 * dimension; the per-scale context is therefore an insertion + rotation
 * mirror of the TEXT context, but kept as a separate variant for
 * round-trip group-code parity (subclass marker `AcDbFcf...` differs).
 */
export const annotScaleToleranceContextSchema = annotScaleBaseSchema.extend({
  kind: z.literal("tolerance"),
  insertion: annotScalePointSchema,
  rotation: z.number(),
});

/**
 * Discriminated union of all 10 per-scale context variants. Discriminator
 * is `kind` (string literal). `z.discriminatedUnion` requires every
 * variant to be an object schema with the same literal key, which all of
 * the variants above satisfy.
 */
export const annotScaleContextSchema = z.discriminatedUnion("kind", [
  annotScaleTextContextSchema,
  annotScaleMtextContextSchema,
  annotScaleMtextAttributeContextSchema,
  annotScaleAttributeContextSchema,
  annotScaleBlockrefContextSchema,
  annotScaleHatchContextSchema,
  annotScaleDimensionContextSchema,
  annotScaleMleaderContextSchema,
  annotScaleLeaderContextSchema,
  annotScaleToleranceContextSchema,
]);

export type AnnotScaleContext = z.infer<typeof annotScaleContextSchema>;
export type AnnotScaleTextContext = z.infer<typeof annotScaleTextContextSchema>;
export type AnnotScaleMtextContext = z.infer<typeof annotScaleMtextContextSchema>;
export type AnnotScaleMtextAttributeContext = z.infer<
  typeof annotScaleMtextAttributeContextSchema
>;
export type AnnotScaleAttributeContext = z.infer<
  typeof annotScaleAttributeContextSchema
>;
export type AnnotScaleBlockrefContext = z.infer<
  typeof annotScaleBlockrefContextSchema
>;
export type AnnotScaleHatchContext = z.infer<typeof annotScaleHatchContextSchema>;
export type AnnotScaleDimensionContext = z.infer<
  typeof annotScaleDimensionContextSchema
>;
export type AnnotScaleMleaderContext = z.infer<
  typeof annotScaleMleaderContextSchema
>;
export type AnnotScaleLeaderContext = z.infer<
  typeof annotScaleLeaderContextSchema
>;
export type AnnotScaleToleranceContext = z.infer<
  typeof annotScaleToleranceContextSchema
>;
